import { getImageId, getImageList } from '@/services/apis/aiwriter'
import { IAIImageChatInfo, IRichAIImagePrompt } from '@/types/aiImageChat'
import { cloneDeep } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { v4 as uuidV4 } from 'uuid'

// 轮询图片url最高次数
const pollingCount = 100
const DEV_AI_IMAGE_MODE_KEY = 'debugAiImageStatus'
const DEV_AI_IMAGE_URL_KEY = 'debugAiImageImageUrl'

type DevAIImageStatus = 'off' | 'normal' | 'success' | 'failed'

const getDevAIImageStatus = (): DevAIImageStatus => {
  if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined') {
    return 'off'
  }
  const raw = (window.localStorage.getItem(DEV_AI_IMAGE_MODE_KEY) || '').toLowerCase()
  if (raw === 'success' || raw === 'failed' || raw === 'normal' || raw === 'off') {
    return raw
  }
  return 'off'
}

const getDevAIImageUrl = () => {
  if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined') {
    return ''
  }
  return window.localStorage.getItem(DEV_AI_IMAGE_URL_KEY) || 'https://picsum.photos/600/400'
}

const useAIImage = () => {
  const [chatInfoList, setChatInfoList] = useState<IAIImageChatInfo[]>([]) // 聊天列表
  const [requesting, setRequesting] = useState(false) // 请求状态
  const chatInfoListRef = useRef<IAIImageChatInfo[]>([])
  const imageIdListRef = useRef<Array<string>[]>([])
  const requestingRef = useRef(false)
  const requestIdsRef = useRef<{ [key: string]: boolean }>({}) // 每次请求的id,其值为flase表示被中断

  const currentRequestId = useRef('') // 当前请求的id
  const pollingTimerRef = useRef<any>(null) // 轮询timer
  const generatingImageIdRef = useRef('') // 当前正在生成的图片id
  const [AIGCImageRequesting, setAIGCImageRequesting] = useState(false) // 是否正在请求AIGC图片

  const [richAIImagePrompt, setRichAIImagePrompt] = useState<IRichAIImagePrompt>({
    use: false, // 是否需要使用
    check: false, // 是否需要检查
    prompot: '', // AIGC图片的prompt
    timestamp: 0,  
  }) // AIGC图片的prompt

  useEffect(() => {
    requestingRef.current = requesting
  }, [requesting])

  useEffect(() => {
    // 兜底处理
    chatInfoListRef.current = chatInfoList
  }, [chatInfoList])

  /**
   * 旧逻辑，暂时保留
   * 
   
  // 轮询图片url  (旧逻辑)
  const pollingImageUrlList = async () => {
    if(imageIdListRef.current.length === 0){
      return
    }
    const imageIds = imageIdListRef.current.join(',')
    try {
      const res: any = await getImageList({ id: imageIds })
      const imageUrlList = res.image_list || []
      imageUrlList.forEach((image: any) => {
        const id = image.material_id
        const chatItem = chatInfoListRef.current.find(item => item.answer === id)
        if(chatItem) {
          if(image.image_url) {
            chatItem.answer = image.image_url
            chatItem.answerStatus = 'success'
            imageIdListRef.current = imageIdListRef.current.filter(imageId => imageId !== id)
          } else {
            chatItem.count = chatItem.count || 0
            if(chatItem.count >= pollingCount) {
              // 超过100次默认失败
              chatItem.answer = ''
              chatItem.answerStatus = 'failed'
              imageIdListRef.current = imageIdListRef.current.filter(imageId => imageId !== id)
            } else {
              chatItem.count += 1
              imageIdListRef.current = imageIdListRef.current.map(item => item)
            }
          }
        }
      })
    } catch (e) {
      console.log(e)
    } finally {
      setChatInfoList(cloneDeep(chatInfoListRef.current))
      if(imageIdListRef.current.length > 0){
        if(pollingTimerRef.current) {
          window.clearTimeout(pollingTimerRef.current as NodeJS.Timeout)
        }
        pollingTimerRef.current = setTimeout(() => {
          pollingImageUrlList()
        },3000)
      }
    }
  }
  // （旧逻辑）
  const getAIImage = async (question: string) => {
    const requestId: any = uuidV4()
    currentRequestId.current = requestId
    requestIdsRef.current[requestId] = true
    let lastIndex = chatInfoListRef.current.length - 1

    const answerData: IChatInfo = {
      id: requestId, // 需要保持和请求id一致
      role: ChatRole.ai,
      question: chatInfoListRef.current[lastIndex].question,
      answer: '',
      answerStatus: 'ing',
      count: 0,
    }

    try {
      const res = await getImageId({ text: question })
      // 根据接口文档，getImageId 返回 { status_code: number; data: string }
      // data 字段就是任务 ID（task_id）
      if (res.status_code === 0 && res.data) {
        const imageId = res.data
        imageIdListRef.current = [...imageIdListRef.current, imageId]
        chatInfoListRef.current = [...chatInfoListRef.current, {
          ...answerData,
          answer: imageId,
        }]
      }
    } catch (e) {
      chatInfoListRef.current.push({
        id: requestId, // 需要保持和请求id一致
        role: ChatRole.ai,
        question: chatInfoListRef.current[lastIndex].question,
        answer: '',
        answerStatus: 'failed',
      })
    } finally {
      setRequesting(false)
      setChatInfoList(cloneDeep(chatInfoListRef.current))
      // 请求结束不需要保留请求id
      delete requestIdsRef.current[requestId]
    }
    if(imageIdListRef.current.length > 0){
      if(pollingTimerRef.current) {
        window.clearTimeout(pollingTimerRef.current as NodeJS.Timeout)
        pollingTimerRef.current = null
      }
      pollingImageUrlList()
    }
  }

  */

  // 轮询当前正在生成的图片的url
  const pollingGeneratingImageUrl = async () => {
    console.log(generatingImageIdRef.current)
    if (!generatingImageIdRef.current) {
      return
    }
    try {
      const res = await getImageList({ id: generatingImageIdRef.current })    
      const chatItem = chatInfoListRef.current.find((item) => item.answer === generatingImageIdRef.current)

      if (!chatItem) {
        setAIGCImageRequesting(false)
        generatingImageIdRef.current = ''
        return
      }

      if (res && res.status) {
        const taskStatus = res.status

        if (taskStatus === 'SUCCEED') {
          // 任务成功，解析 data 字段获取图片 URL
          if (res.data) {
            try {
              const resultData = JSON.parse(res.data)
              const imageUrl = resultData.FINAL_IMAGE_URL || ''
              if (imageUrl) {
                chatItem.answer = imageUrl
                chatItem.answerStatus = 'success'
                generatingImageIdRef.current = ''
                setAIGCImageRequesting(false)
              }
            } catch (e) {
              console.error('解析任务结果失败:', e)
            }
          }
        } else if (taskStatus === 'FAILED') {
          // 任务失败，停止轮询
          chatItem.answerStatus = 'failed'
          generatingImageIdRef.current = ''
          setAIGCImageRequesting(false)
        } else {
          // PENDING 或 EXECUTING 状态，继续轮询
          if (chatItem.count! >= pollingCount) {
            chatItem.answerStatus = 'failed'
            generatingImageIdRef.current = ''
            setAIGCImageRequesting(false)
          }
        }
      }

      chatItem.count!++
      setChatInfoList(cloneDeep(chatInfoListRef.current))
    } catch (e) {
      console.log(e)
      generatingImageIdRef.current = ''
      setAIGCImageRequesting(false)
    } finally {
      clearTimeout(pollingTimerRef.current)
      if (generatingImageIdRef.current) {
        pollingTimerRef.current = setTimeout(() => {
          pollingGeneratingImageUrl()
        }, 3000)
      }
    }
  }

  // 获取文生图的图片id
  const getAIGCImageId = async (question: string) => {
    let lastIndex = chatInfoListRef.current.length - 1

    try {
      const res = await getImageId({ text: question })
      if (res) {
        const imageId = String(res)
        generatingImageIdRef.current = imageId
        // chatInfoListRef.current[lastIndex].answerStatus = 'success'
        chatInfoListRef.current[lastIndex].answer = imageId
      } else {
        chatInfoListRef.current[lastIndex].answerStatus = 'failed'
        setAIGCImageRequesting(false)
      }
    } catch (e) {
      chatInfoListRef.current[lastIndex].answerStatus = 'failed'
      setAIGCImageRequesting(false)
    } finally {
      setChatInfoList(cloneDeep(chatInfoListRef.current))
    }
    if (generatingImageIdRef.current) {
      clearTimeout(pollingTimerRef.current)
      pollingGeneratingImageUrl()
    }
  }

  // 发送文生图的问题
  const sendAIGCImageQuestion = (value: string) => {
    setRichAIImagePrompt({ 
      prompot: '', 
      use: false, 
      check: false, 
      timestamp: Date.now()
    }) // 发送问题后，清空富文本AI图片的prompt内容，防止干扰

    const id = uuidV4()
    const data: IAIImageChatInfo = {
      id,
      question: value,
      answer: '',
      answerStatus: 'ing',
      count: 0,
    }
    currentRequestId.current = id

    // 用ref承接一下，防止太快，状态来不及更新
    chatInfoListRef.current = [...chatInfoListRef.current, data]
    setChatInfoList(cloneDeep(chatInfoListRef.current))
    setAIGCImageRequesting(true)

    const devStatus = getDevAIImageStatus()
    if (devStatus === 'success' || devStatus === 'failed') {
      // 开发环境本地调试开关：快速预览成功/失败样式，不依赖后端任务状态。
      setTimeout(() => {
        const target = chatInfoListRef.current.find((item) => item.id === id)
        if (!target) return
        if (devStatus === 'success') {
          target.answerStatus = 'success'
          target.answer = getDevAIImageUrl()
        } else {
          target.answerStatus = 'failed'
          target.answer = ''
        }
        generatingImageIdRef.current = ''
        setAIGCImageRequesting(false)
        setChatInfoList(cloneDeep(chatInfoListRef.current))
      }, 800)
      return
    }

    Promise.resolve().then(() => {
      getAIGCImageId(value)
    })
  }

  // 清空聊天列表
  const clearChatInfoList = () => {
    clearTimeout(pollingTimerRef.current)
    currentRequestId.current = ''
    pollingTimerRef.current = ''
    chatInfoListRef.current = []
    imageIdListRef.current = []
    setAIGCImageRequesting(false)
    setChatInfoList([])
  }

  return {
    chatInfoList,
    setChatInfoList,
    // requesting,
    // setRequesting,
    // requestingRef,
    chatInfoListRef,
    // requestIdsRef,
    generatingImageIdRef,
    currentRequestId,
    AIGCImageRequesting,
    richAIImagePrompt,
    setRichAIImagePrompt,
    setAIGCImageRequesting,
    // getAIImage,
    sendAIGCImageQuestion,
    clearChatInfoList,
  }
}

export default useAIImage